import { useLocation, useNavigate, Link } from "react-router-dom";
import { useState, useLayoutEffect, useEffect } from "react";
import { api, startLifeguideHandoff, createShare, fireEvent } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { usePwa } from "@/context/PwaContext";
import { VerseBlock } from "@/components/VerseBlock";
import { ScriptureCard } from "@/components/ScriptureCard";
import { toast } from "sonner";
import { AlertTriangle, Share2, Sparkles, Send, Image, RefreshCw, BookOpen, ArrowLeft, ArrowRight, Heart } from "lucide-react";

export default function WisdomPath() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const pwa = usePwa();
  const [result, setResult] = useState(loc.state?.result || null);
  const [query, setQuery] = useState(loc.state?.query || null);
  const [verses, setVerses] = useState(loc.state?.result?.verses || []);
  const [busy, setBusy] = useState("");
  const [cardVerse, setCardVerse] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [savedRefs, setSavedRefs] = useState({});

  useEffect(() => { api.get("/site-config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  // Restore a result when LifeGuide returns the user here with safe query params (topic/source).
  useEffect(() => {
    if (result) return;
    const p = new URLSearchParams(loc.search);
    const topic = p.get("topic");
    const src = p.get("source") || "proverbs";
    if (topic) {
      api.post("/wisdom-path", { issues: [topic], source: src })
        .then(({ data }) => { setResult(data); setVerses(data.verses); setQuery({ issues: [topic], situation: "", source: src }); })
        .catch(() => {});
    }
  }, []);

  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    const toTop = () => { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); if (document.documentElement) document.documentElement.scrollTop = 0; };
    toTop();
    const t = setTimeout(toTop, 60);
    if (result) track("result_view", { path: "/wisdom-path", topic: result.labels?.[0] || result.issues?.[0], proverb_id: verses?.[0]?.id });
    // After the visitor has meaningfully used the site (first result), gently offer "Save to Phone".
    if (result) { const id = setTimeout(() => pwa?.maybePrompt(), 2500); return () => { clearTimeout(t); clearTimeout(id); }; }
    return () => clearTimeout(t);
  }, [result]);

  if (!result) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center">
        <h2 className="font-serif-display text-3xl text-[#0F172A]">No wisdom yet</h2>
        <p className="text-[#475569] mt-3">Tell us what you are dealing with and we'll find biblical wisdom for you.</p>
        <Link to="/" className="inline-block mt-6 px-6 py-3 rounded-full bg-[#2C4C3B] text-white font-semibold">Start here</Link>
      </div>
    );
  }

  const curQuery = query || { issues: result.issues || [], situation: "", need: result.need, source: result.source || "proverbs" };
  const primaryLabel = result.primary_topic?.label || result.labels?.[0] || "";
  const lgContext = () => ({
    topic: primaryLabel,
    subtopic: query?.subtopic || "",
    selected_issues: result.labels || [],
    scripture_references: verses.map((v) => v.reference),
    guidance_summary: result.intro || "",
    // Safe return route (slugs only) so LifeGuide can bring the user back to this result.
    return_path: `/wisdom-path?topic=${encodeURIComponent(curQuery.issues?.[0] || "")}&source=${encodeURIComponent(curQuery.source || "proverbs")}`,
  });

  const runSearch = async (extra, { replace = true } = {}) => {
    setBusy(extra.mode || "search");
    try {
      const payload = { issues: curQuery.issues, situation: curQuery.situation,
        needs: curQuery.need ? [curQuery.need] : [], source: curQuery.source, ...extra };
      const { data } = await api.post("/wisdom-path", payload);
      if (replace) { setResult(data); setVerses(data.verses); setQuery({ ...curQuery, ...(extra.source ? { source: extra.source } : {}) }); }
      else {
        const existing = new Set(verses.map((v) => v.reference));
        const add = data.verses.filter((v) => !existing.has(v.reference));
        if (add.length === 0) toast.info("That's all we have for this right now.");
        setVerses((vs) => [...vs, ...add]);
      }
      return data;
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setBusy(""); }
  };

  const showAnother = async () => {
    track("show_another", { path: "/wisdom-path", topic: primaryLabel });
    await runSearch({ exclude: verses.map((v) => v.id), limit: 3 }, { replace: false });
  };
  const moreWisdom = async () => {
    track("more_wisdom", { path: "/wisdom-path", topic: primaryLabel });
    await runSearch({ source: "wisdom", exclude: verses.map((v) => v.id), limit: 4 }, { replace: false });
  };
  const stayWith = async (sub) => {
    setQuery({ ...curQuery, situation: sub, subtopic: sub });
    const data = await runSearch({ situation: sub });
    if (data) window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveWisdom = async (v) => {
    if (!user) { toast.error("Log in to save wisdom to your account."); nav("/login"); return; }
    try {
      await api.post("/saved-wisdom", { reference: v.reference, text: v.text, translation: v.translation,
        topic: primaryLabel, explanation: v.explanation || "" });
      setSavedRefs((s) => ({ ...s, [v.reference]: true }));
      toast.success("Saved to My Wisdom.");
    } catch { toast.error("Could not save."); }
  };

  const shareOne = async (v) => {
    try {
      const s = await createShare({ issues: result.issues, labels: result.labels, verses: [v], anonymous: false });
      await navigator.clipboard.writeText(`${v.text} — ${v.reference}\n${s.url}`);
      fireEvent("share_copied");
      track("share", { path: "/wisdom-path", topic: primaryLabel, channel: "copy" });
      toast.success("Link copied — share it with someone.");
    } catch { toast.error("Could not create a share link."); }
  };

  const lifeguide = async () => {
    track("bridge_click", { path: "/wisdom-path", topic: primaryLabel });
    try {
      const r = await startLifeguideHandoff(lgContext());
      if (!r.ok && r.reason === "not_configured") toast.error("Could not open LifeGuide right now. Please try again.");
      else if (!r.ok) toast.error("Could not start the LifeGuide handoff. Please try again.");
      else fireEvent("handoff_success");
    } catch (e) {
      if (e.response?.status === 401) { toast.error("Please log in to continue with LifeGuide."); nav("/login"); }
      else toast.error("LifeGuide handoff failed. Please try again in a moment.");
    }
  };

  const subtopics = result.subtopics || [];

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
      <button onClick={() => nav("/")} className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#2C4C3B] mb-6"><ArrowLeft className="w-4 h-4" /> New search</button>

      {result.safety && (
        <div data-testid="safety-message" className="rounded-2xl border border-red-200 bg-red-50 px-6 py-5 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800 mb-1">You matter, and help is available right now.</h3>
              <p className="text-sm text-red-700 leading-relaxed">It sounds like you may be facing something serious. Please reach out immediately to a qualified professional or emergency service. In the U.S. call or text <strong>988</strong>, or dial <strong>911</strong>. This app offers Scripture-based encouragement and is not a substitute for professional or emergency help.</p>
            </div>
          </div>
        </div>
      )}

      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#B8912F] mb-3">Biblical wisdom for you</p>
        {result.labels?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {result.labels.map((l) => <span key={l} className="text-sm px-3 py-1 rounded-full bg-[#2C4C3B] text-white">{l}</span>)}
          </div>
        )}
        <p className="font-serif-display text-2xl sm:text-3xl leading-snug text-[#0F172A]">{result.intro}</p>
        {result.ai_used && <p className="text-xs text-[#94A3B8] mt-3">✓ Matched with AI situation analysis. Every verse below is real Scripture from the World English Bible.</p>}
        {result.source_note && <p data-testid="source-note" className="text-xs text-[#94A3B8] mt-2 italic">{result.source_note}</p>}
      </header>

      {/* Result verses, each with quick actions */}
      <div className="space-y-6">
        {verses.map((v, i) => (
          <div key={v.reference + i}>
            <VerseBlock v={v} index={i} />
            <div className="flex flex-wrap gap-2 mt-2 px-1 print:hidden">
              <MiniBtn testid={`save-wisdom-${i}`} icon={Heart} label={savedRefs[v.reference] ? "Saved" : "Save"} onClick={() => saveWisdom(v)} active={savedRefs[v.reference]} />
              <MiniBtn testid={`send-verse-${i}`} icon={Send} label="Send to Someone" onClick={() => nav("/send")} />
              <MiniBtn testid={`card-verse-${i}`} icon={Image} label="Make a Card" onClick={() => setCardVerse(v)} />
              <MiniBtn testid={`share-verse-${i}`} icon={Share2} label="Share" onClick={() => shareOne(v)} />
            </div>
          </div>
        ))}
      </div>

      {/* Discovery actions */}
      <div className="flex flex-wrap gap-2 mt-6 print:hidden">
        <button data-testid="show-another-btn" onClick={showAnother} disabled={!!busy}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-sm font-semibold hover:bg-[#F1F5F1] transition-colors disabled:opacity-60"><RefreshCw className={`w-4 h-4 ${busy === "search" ? "animate-spin" : ""}`} /> Show Me Another</button>
        <button data-testid="more-wisdom-btn" onClick={moreWisdom} disabled={!!busy}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0F172A] text-white text-sm font-semibold hover:bg-black transition-colors disabled:opacity-60"><BookOpen className="w-4 h-4" /> More Biblical Wisdom</button>
      </div>

      {/* Stay with this topic */}
      {subtopics.length > 0 && (
        <div data-testid="stay-with-topic" className="mt-10 rounded-2xl border border-[#D9E2DA] bg-[#F1F5F1] px-6 py-6 print:hidden">
          <p className="text-xs uppercase tracking-[0.2em] text-[#B8912F] font-semibold mb-1">Stay with this topic</p>
          <p className="font-serif-display text-xl text-[#0F172A] mb-3">Keep exploring {primaryLabel ? primaryLabel.toLowerCase() : "this"}</p>
          <div className="flex flex-wrap gap-2">
            {subtopics.map((s) => (
              <button key={s} data-testid={`subtopic-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => stayWith(s)}
                className="px-4 py-2 rounded-full bg-white border border-[#E2E8F0] text-[#475569] text-sm font-medium hover:border-[#2C4C3B] transition-colors">{s}</button>
            ))}
          </div>
        </div>
      )}

      {/* Smart LifeGuide bridge (only after real value) */}
      <div className="mt-8 rounded-2xl border border-[#E2E8F0] bg-white px-6 py-7 print:hidden">
        <p className="text-xs uppercase tracking-widest text-[#B8912F] mb-2">Need more than a verse?</p>
        <h3 className="font-serif-display text-2xl text-[#0F172A]">{cfg?.bridge_headline || "Finding the right Scripture is the beginning."}</h3>
        <p className="text-[#475569] mt-1 leading-relaxed">{cfg?.bridge_sub || "Want help applying this to what you're actually facing?"}</p>
        {cfg?.bridge_points?.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {cfg.bridge_points.map((p) => <li key={p} className="flex items-start gap-2 text-sm text-[#475569]"><Sparkles className="w-4 h-4 text-[#B8912F] shrink-0 mt-0.5" /> {p}</li>)}
          </ul>
        )}
        <button data-testid="wisdom-lifeguide-btn" onClick={lifeguide} className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#2C4C3B] text-[#2C4C3B] font-semibold hover:bg-[#F1F5F1] transition-colors">{cfg?.bridge_button || "Continue this in LifeGuide"} <ArrowRight className="w-4 h-4" /></button>
      </div>

      {/* After sharing: help someone */}
      <div className="mt-6 rounded-2xl bg-[#F1F5F1] px-6 py-6 text-center print:hidden">
        <p className="font-serif-display text-xl text-[#0F172A]">{cfg?.share_followup_headline || "Trying to help someone you care about?"}</p>
        <p className="text-sm text-[#475569] mt-1 max-w-xl mx-auto">{cfg?.share_followup_sub || "Send them Scripture here for free."}</p>
        <div className="mt-4 flex flex-wrap gap-2 justify-center">
          <button data-testid="send-someone-btn" onClick={() => nav("/send")} className="px-5 py-2.5 rounded-full bg-[#2C4C3B] text-white text-sm font-semibold">Send Wisdom — free</button>
          <button data-testid="ask-lifeguide-btn" onClick={lifeguide} className="px-5 py-2.5 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-sm font-semibold hover:bg-white transition-colors">{cfg?.share_followup_button || "Ask LifeGuide"}</button>
        </div>
      </div>

      {cardVerse && <ScriptureCard verse={cardVerse} onClose={() => setCardVerse(null)} />}
    </div>
  );
}

function MiniBtn({ testid, icon: Icon, label, onClick, active }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${active ? "bg-[#2C4C3B] text-white" : "border border-[#E2E8F0] text-[#475569] hover:border-[#2C4C3B]"}`}>
      <Icon className={`w-4 h-4 ${active ? "fill-white" : ""}`} /> {label}
    </button>
  );
}
